import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Mic, MessageCircle, Gift, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicFooter from "@/components/PublicFooter";
import { OFFERS } from "@/lib/offers";

const LOGO = "/brand/mtg_logo.svg";

const CONVOS = [
  { q: "Nenzio, qu'est-ce qui mérite mon attention aujourd'hui ?", a: "Tu as 3 devis à relancer, 2 nouvelles demandes et deux rendez-vous cet après-midi." },
  { q: "Nenzio, comment s'est passée la semaine ?", a: "27 demandes reçues, 19 devis envoyés et 11 acceptés. 4 devis sont toujours en attente." },
  { q: "Nenzio, relance les devis en attente.", a: "Je peux préparer les relances. Tu veux les vérifier avant leur envoi ?" },
];

const CHOICES = [
  { name: "ESSENTIEL", text: "Pour qualifier les demandes, préparer les devis et faciliter la prise de rendez-vous." },
  { name: "PRO", text: "Pour automatiser davantage le suivi commercial." },
  { name: "NENZIO", text: "Pour être accompagné dans le pilotage quotidien de son activité." },
];

const FAQ = [
  { q: "Puis-je payer mensuellement ?", a: "Oui. Les offres peuvent être proposées en paiement annuel ou mensuel. La formule annuelle bénéficie d'un tarif préférentiel." },
  { q: "En quoi consiste l'offre de lancement ?", a: "Les forfaits Essentiel et Pro peuvent bénéficier de 3 mois offerts. Selon les conditions d'éligibilité, cette période peut être étendue jusqu'à 6 mois. Nenzio n'est pas inclus dans cette offre." },
  { q: "La configuration est-elle incluse ?", a: "La configuration standard de MonTempsGagné est incluse selon l'offre choisie. Les intégrations ou développements spécifiques peuvent faire l'objet d'un devis complémentaire." },
  { q: "Puis-je changer d'offre ?", a: "Oui. L'offre peut évoluer en fonction des besoins de l'entreprise." },
  { q: "MonTempsGagné est-il réservé à la serrurerie automobile ?", a: "Non. La serrurerie automobile sert actuellement de démonstration, mais MonTempsGagné est conçu pour s'adapter progressivement à différents métiers de l'automobile." },
  { q: "Qu'est-ce que Nenzio ?", a: "Nenzio est le futur copilote IA de MonTempsGagné. Son objectif est d'analyser l'activité, identifier les priorités et permettre au professionnel de piloter certaines actions simplement en discutant avec lui." },
  { q: "Nenzio est-il déjà disponible ?", a: "Nenzio est actuellement en préparation. Les fonctionnalités présentées illustrent la direction prévue pour cette évolution de MonTempsGagné." },
];

function OfferCard({ offer, annual }) {
  return (
    <div
      data-testid={`offer-${offer.id}`}
      className={
        "relative flex flex-col min-w-0 rounded-3xl p-6 md:p-8 " +
        (offer.dark
          ? "montempsgagne-bg-navy text-white shadow-[0_30px_80px_-40px_rgba(16,50,79,0.6)]"
          : offer.highlight
          ? "bg-white ring-2 ring-[#19C3B3] shadow-[0_30px_80px_-40px_rgba(25,195,179,0.45)]"
          : "bg-white border border-slate-100 shadow-[0_20px_60px_-40px_rgba(16,50,79,0.3)]")
      }
    >
      {offer.badge && (
        <span
          className={
            "absolute -top-3 left-7 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide " +
            (offer.dark ? "bg-[#19C3B3] text-[#0B1B2B]" : "bg-[#16324F] text-white")
          }
          data-testid={`badge-${offer.id}`}
        >
          {offer.badge}
        </span>
      )}
      <div className="flex items-center gap-2">
        {offer.dark && <Sparkles className="h-5 w-5 text-[#19C3B3]" />}
        <h3 className={"font-display text-2xl font-extrabold " + (offer.dark ? "text-white" : "text-[#16324F]")}>{offer.name}</h3>
      </div>
      {offer.subtitle && <div className="mt-1 text-sm font-semibold text-[#19C3B3]">{offer.subtitle}</div>}
      <p className={"mt-3 text-sm leading-relaxed " + (offer.dark ? "text-white/75" : "text-slate-600")}>{offer.promise}</p>
      {offer.promoBadge && (
        <div className="mt-3" data-testid={`promo-${offer.id}`}>
          <span className="inline-block rounded-full bg-[#19C3B3]/15 text-[#0E5A56] text-[11px] font-bold uppercase tracking-wide px-2.5 py-1">
            {offer.promoBadge}
          </span>
          {offer.promoSub && <div className="mt-1 text-[11px] text-slate-400">{offer.promoSub}</div>}
        </div>
      )}

      <div className="mt-6">
        {offer.prefix && (
          <div className={"text-sm font-semibold " + (offer.dark ? "text-white/70" : "text-slate-500")}>{offer.prefix}</div>
        )}
        <div className={"font-display text-4xl font-extrabold flex items-baseline gap-1.5 " + (offer.dark ? "text-white" : "text-[#16324F]")} data-testid={`price-${offer.id}`}>
          {annual ? offer.annualPerMonth : offer.monthlyLabel}
          <span className={"text-base font-semibold " + (offer.dark ? "text-white/60" : "text-slate-400")}>{offer.suffix}</span>
        </div>
        {annual && (
          <div className={"mt-1 text-xs " + (offer.dark ? "text-white/60" : "text-slate-500")} data-testid={`billed-${offer.id}`}>
            {offer.annualBilled}
          </div>
        )}
        {annual && offer.annualEconomy && (
          <div className="mt-1 text-xs font-semibold text-[#0E8F82]">{offer.annualEconomy}</div>
        )}
      </div>

      <a href={offer.ctaHref} className="mt-6 block" data-testid={`cta-${offer.id}`}>
        <Button
          className={
            "w-full h-12 px-6 rounded-full text-sm md:text-[15px] font-semibold " +
            (offer.dark
              ? "bg-[#19C3B3] text-[#0B1B2B] hover:bg-[#15b0a1]"
              : offer.highlight
              ? "bg-[#16324F] text-white hover:bg-[#1d4368]"
              : "bg-white text-[#16324F] border border-[#16324F]/25 hover:bg-[#16324F] hover:text-white")
          }
        >
          {offer.cta} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </a>

      {offer.includes && <div className={"mt-6 text-xs font-semibold " + (offer.dark ? "text-white/80" : "text-slate-500")}>{offer.includes}</div>}
      <ul className={"mt-4 space-y-2.5 text-sm " + (offer.dark ? "text-white/85" : "text-slate-700")}>
        {offer.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#19C3B3]" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {offer.note && (
        <p className={"mt-5 text-xs leading-relaxed " + (offer.dark ? "text-white/60" : "text-slate-400")} data-testid={`note-${offer.id}`}>
          {offer.note}
        </p>
      )}
    </div>
  );
}

export default function Tarifs() {
  const [annual, setAnnual] = useState(true);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Tarifs MonTempsGagné | Automatisation pour professionnels de l'automobile";
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "description"); document.head.appendChild(meta); }
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute("content", "Découvrez les offres MonTempsGagné pour automatiser les demandes, les devis et les rendez-vous des professionnels de l'automobile.");
    return () => {
      document.title = prevTitle;
      if (created) meta.remove(); else if (prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="page-tarifs">
      <nav className="w-full flex items-center justify-between px-6 md:px-12 py-5">
        <Link to="/" className="flex items-center gap-2" data-testid="tarifs-logo"><img src={LOGO} alt="MonTempsGagné" className="h-9 md:h-10" /></Link>
        <div className="flex items-center gap-3 md:gap-5">
          <Link to="/tarifs" className="text-sm font-semibold text-[#19C3B3]" data-testid="tarifs-nav-tarifs">Tarifs</Link>
          <Link to="/demo" className="text-sm font-semibold text-[#16324F] hover:text-[#19C3B3] transition-base" data-testid="tarifs-nav-demo">Démo</Link>
        </div>
      </nav>

      {/* HERO + TOGGLE */}
      <header className="px-6 md:px-12 pt-6 pb-2 max-w-6xl mx-auto w-full text-center">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#16324F] tracking-tight">Des tarifs clairs, pensés pour gagner du temps</h1>
        <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
          Choisissez le niveau d'automatisation adapté à votre activité automobile. Sans surprise, sans engagement compliqué.
        </p>
        <div className="mt-8 inline-flex items-center rounded-full bg-[#EAF1F7] p-1" data-testid="billing-toggle">
          <button
            onClick={() => setAnnual(true)}
            className={"h-10 px-5 rounded-full text-sm font-semibold transition-base " + (annual ? "bg-[#16324F] text-white shadow" : "text-[#16324F]")}
            data-testid="toggle-annual"
          >
            Annuel
          </button>
          <button
            onClick={() => setAnnual(false)}
            className={"h-10 px-5 rounded-full text-sm font-semibold transition-base " + (!annual ? "bg-[#16324F] text-white shadow" : "text-[#16324F]")}
            data-testid="toggle-monthly"
          >
            Mensuel
          </button>
        </div>
      </header>

      {/* OFFRE DE LANCEMENT */}
      <section className="px-6 md:px-12 pt-8 max-w-6xl mx-auto w-full">
        <div className="rounded-3xl border border-[#19C3B3]/30 bg-[#19C3B3]/8 p-6 md:p-7" data-testid="launch-offer">
          <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#0E5A56]">
                  <Gift className="h-3.5 w-3.5 text-[#19C3B3]" /> Offre de lancement
                </div>
                <span className="inline-flex items-center rounded-full bg-[#16324F] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white" data-testid="launch-offer-badge">
                  Jusqu'à 6 mois offerts*
                </span>
              </div>
              <h2 className="mt-3 font-display text-2xl md:text-3xl font-extrabold text-[#16324F]">3 mois offerts sur Essentiel et Pro</h2>
              <p className="mt-2 text-sm md:text-base text-slate-600 leading-relaxed max-w-2xl">
                Découvrez MonTempsGagné pendant 3 mois sans abonnement. Et jusqu'à 6 mois offerts sous conditions.
              </p>
              <p className="mt-2 text-xs text-slate-400 max-w-2xl" data-testid="launch-offer-mention">
                Offre valable sur les forfaits Essentiel et Pro. Nenzio n'est pas inclus dans cette offre.
              </p>
              <details className="mt-3 group" data-testid="launch-offer-details">
                <summary className="cursor-pointer list-none inline-flex items-center gap-1 text-sm font-semibold text-[#16324F]">
                  Voir les conditions
                  <span className="text-[#19C3B3] transition-transform group-open:rotate-45 text-lg leading-none">+</span>
                </summary>
                <ul className="mt-3 space-y-1.5 text-xs text-slate-500 leading-relaxed">
                  <li>• Réservée aux nouveaux clients des offres Essentiel et Pro.</li>
                  <li>• Les 3 mois commencent à la mise en service effective.</li>
                  <li>• En formule annuelle, la période payante de 12 mois commence après les 3 mois offerts.</li>
                  <li>• Le paramétrage standard est inclus ; les développements spécifiques font l'objet d'un devis.</li>
                  <li>• L'offre prescripteurs remplace l'offre découverte standard et ne s'y additionne pas.</li>
                </ul>
              </details>
            </div>
            <a href="/#evaluer" className="w-full md:w-auto md:shrink-0" data-testid="launch-offer-cta">
              <Button className="w-full md:w-auto h-12 px-6 md:px-8 rounded-full bg-[#16324F] text-white hover:bg-[#1d4368] text-sm md:text-[15px] font-semibold">
                Demander une démonstration <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* OFFERS */}
      <section className="px-6 md:px-12 pt-8 pb-4 max-w-6xl mx-auto w-full">
        <div className="grid gap-6 md:grid-cols-3 items-start">
          {OFFERS.map((o) => <OfferCard key={o.id} offer={o} annual={annual} />)}
        </div>
        <p className="mt-6 text-center text-xs text-slate-400">
          Tarifs indicatifs hors taxes (HT). Nenzio est en préparation : les tarifs sont donnés à titre indicatif. Certaines automatisations avancées de l'offre Pro sont déployées progressivement et incluses dans l'accompagnement au paramétrage.
        </p>
      </section>

      {/* NENZIO EXPLAINER */}
      <section id="nenzio" className="montempsgagne-bg-soft scroll-mt-16 py-16 md:py-24 px-6 md:px-12 mt-8">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#16324F]"><Sparkles className="h-4 w-4 text-[#19C3B3]" /> Nenzio · Bientôt disponible</div>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-extrabold text-[#16324F]">MonTempsGagné automatise. Nenzio vous aide à piloter.</h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Pendant que MonTempsGagné automatise les demandes, les devis et les rendez-vous, Nenzio analyse votre activité et vous indique ce qui mérite votre attention.
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {CONVOS.map((c, i) => (
              <div key={i} className="rounded-2xl bg-white p-5 border border-slate-100 shadow-[0_20px_50px_-40px_rgba(16,50,79,0.4)]" data-testid={`nenzio-convo-${i}`}>
                <div className="flex items-start gap-2">
                  <MessageCircle className="h-4 w-4 mt-1 text-slate-400 shrink-0" />
                  <p className="text-sm font-semibold text-[#16324F]">{c.q}</p>
                </div>
                <div className="mt-3 flex items-start gap-2">
                  <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#16324F] text-[10px] font-bold text-[#19C3B3] shrink-0">N</span>
                  <p className="text-sm text-slate-600">{c.a}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-400 flex items-center gap-1"><Mic className="h-3.5 w-3.5" /> Exemples illustratifs. Nenzio n'est pas encore disponible et n'est pas connecté à des données réelles.</p>
        </div>
      </section>

      {/* WHICH OFFER */}
      <section className="py-16 md:py-20 px-6 md:px-12 max-w-6xl mx-auto w-full">
        <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#16324F] text-center">Quelle offre choisir ?</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {CHOICES.map((c) => (
            <div key={c.name} className="kf-card p-6">
              <div className="font-display text-lg font-bold text-[#16324F]">{c.name}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMME PRESCRIPTEURS */}
      <section className="py-4 px-6 md:px-12 max-w-6xl mx-auto w-full" data-testid="section-prescripteurs">
        <div className="rounded-3xl border border-slate-200 bg-white p-7 md:p-10 shadow-[0_20px_60px_-45px_rgba(16,50,79,0.3)]">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FF7A59]">
            <Handshake className="h-4 w-4" /> Programme prescripteurs
          </div>
          <h2 className="mt-3 font-display text-2xl md:text-3xl font-extrabold text-[#16324F]">Vous accompagnez des professionnels de l'automobile ?</h2>
          <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">
            Faites découvrir MonTempsGagné à votre réseau et bénéficiez, en tant que partenaire prescripteur, de jusqu'à 6 mois offerts sur Essentiel.
          </p>
          <ul className="mt-6 space-y-2.5 text-sm text-slate-700 max-w-2xl">
            {[
              "3 mois offerts à la mise en service.",
              "3 mois supplémentaires après qu'un premier client recommandé est devenu payant.",
              "Pour certains partenariats stratégiques, les 6 mois peuvent être accordés dès le départ, après validation d'un accord de partenariat.",
            ].map((c) => (
              <li key={c} className="flex items-start gap-2.5">
                <Check className="h-4 w-4 mt-0.5 shrink-0 text-[#19C3B3]" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
          <a href="/?sujet=partenaire#evaluer" className="mt-7 block w-full md:inline-block md:w-auto" data-testid="prescripteur-cta">
            <Button className="w-full md:w-auto h-12 px-6 md:px-8 rounded-full bg-[#16324F] text-white hover:bg-[#1d4368] text-sm md:text-[15px] font-semibold">
              Devenir partenaire prescripteur <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      {/* ROI */}
      <section className="montempsgagne-bg-soft py-16 md:py-20 px-6 md:px-12 mt-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#16324F]">Quelques heures gagnées peuvent déjà faire la différence.</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            MonTempsGagné est conçu pour réduire le temps consacré aux demandes répétitives, aux devis, aux rendez-vous et au suivi administratif. L'objectif : consacrer davantage de temps au métier, aux clients et au développement de l'activité.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 px-6 md:px-12 max-w-3xl mx-auto w-full">
        <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#16324F] text-center">Questions fréquentes</h2>
        <div className="mt-8 space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} className="group rounded-2xl border border-slate-100 bg-white p-5" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer list-none flex items-center justify-between gap-3 font-semibold text-[#16324F]">
                {f.q}
                <span className="text-[#19C3B3] transition-transform group-open:rotate-45 text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="mt-10 text-center">
          <a href="/#evaluer" data-testid="faq-cta"><Button className="h-12 px-6 md:px-8 rounded-full bg-[#16324F] text-white hover:bg-[#1d4368] text-sm md:text-[15px] font-semibold">Demander une démonstration <ArrowRight className="ml-2 h-4 w-4" /></Button></a>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
